import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  NavigationExtras,
  Router,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import {
  BackNavigationService,
  CommandStore,
  SOFTLINE_FEATURE_COMMANDS,
  TitleService,
} from '@softline/application';
import { Store } from '@softline/core';
import {
  IconComponent,
  IsDefinedNotEmptyPipe,
  MessageBarContainerComponent,
  ToolTipDirective,
  TranslatePipe,
  VarDirective,
} from '@softline/ui-core';
import { asapScheduler, observeOn } from 'rxjs';
import { MenuOutletComponent } from '../../menu/menu-outlet/menu-outlet.component';
import { BottomBarComponent } from '../components/bottom-bar/bottom-bar.component';
import { ShellEnvironment } from '../shell.constants';
import { SOFTLINE_TOKEN_SHELL_ENVIRONMENT } from '../shell.tokens';
import { EmbeddedShellService } from './embedded-shell.service';

@Component({
  selector: 'soft-embedded-shell',
  imports: [
    RouterOutlet,
    MenuOutletComponent,
    AsyncPipe,
    TranslatePipe,
    ToolTipDirective,
    VarDirective,
    RouterLinkActive,
    NgClass,
    IconComponent,
    IsDefinedNotEmptyPipe,
    BottomBarComponent,
    MessageBarContainerComponent,
  ],
  providers: [
    { provide: SOFTLINE_TOKEN_SHELL_ENVIRONMENT, useValue: ShellEnvironment.EMBEDDED },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex items-center justify-between w-full border-b border-b-border bg-surface z-10 min-h-12.25"
    >
      <div class="flex items-center">
        @if (backNavigationService.canNavigateBack() && !embeddedShellService.isRoot()) {
          <div
            class="size-12 border-r min-w-12 aspect-square relative flex items-center justify-center hover:bg-hover cursor-pointer text-primary"
            (click)="backNavigationService.navigateBack()"
          >
            <soft-icon name="fa-regular fa-arrow-left !text-2xl" />
          </div>
        }

        @let title = titleService.title();
        @if (title | isDefinedNotEmpty) {
          <div class="flex flex-col items-start justify-center ml-3 w-full">
            <div
              class="text-base font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
            >
              {{ titleService.title() }}
            </div>

            @let subtitle = titleService.subtitle();

            @if (subtitle | isDefinedNotEmpty) {
              <div
                class="text-sm opacity-60 whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {{ subtitle }}
              </div>
            }
          </div>
        }
      </div>
      <div class="flex items-center justify-end shrink-0">
        <soft-menu-outlet name="action" />
        @for (command of topCommands$ | async; track command.name) {
          @let visible = command.isVisible ? (command.isVisible | async) : true;
          @let canExecute = command.canExecute ? (command.canExecute | async) : true;
          @if (visible) {
            <div
              [id]="'framework-application-shell-actions-commands-top-' + command.name"
              class="size-12 min-w-12 aspect-square relative flex items-center justify-center hover:bg-hover cursor-pointer text-primary"
              softToolTip="{{ command.name | translate }}"
              toolTipPosition="bottom"
              [toolTipVisible]="true"
              [routerLinkActive]="command.routerLink ? 'active' : ''"
              [routerLinkActiveOptions]="{ exact: true }"
              [class.disabled]="!canExecute"
              [ngClass]="command.class"
              (click)="
                onClick(
                  $safeNavigationMigration(command?.execute),
                  $safeNavigationMigration(command?.routerLink),
                  canExecute ?? false,
                  $safeNavigationMigration(command?.routerLinkParams)
                )
              "
            >
              @let badge = command?.badge ? (command.badge | async) : undefined;
              @if (badge) {
                <span
                  class="soft-badge absolute top-0.5! right-0.5! text-[8px]! font-semibold"
                >
                  1
                </span>
              }
              <soft-icon [name]="command.icon" class="text-2xl!" />
            </div>
          }
        }
      </div>
    </div>
    <div class="embedded-shell-content">
      <router-outlet class="contents" />
    </div>
    <soft-bottom-bar class="contents" />
    <div class="shell-message-bar-wrapper">
      <soft-message-bar-container />
    </div>
  `,
  styles: `
    :host {
      background: var(--shellContentBackground);
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto;
      width: 100%;
      max-height: 100vh;
      min-height: 100vh;
      overflow: hidden;
    }

    .embedded-shell-content {
      min-height: 0;
      overflow-y: auto;
    }
  `,
})
export default class EmbeddedShellComponent {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly embeddedShellService = inject(EmbeddedShellService);
  readonly backNavigationService = inject(BackNavigationService);
  readonly titleService = inject(TitleService);

  readonly topCommands$ = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'action-menu-top')
    .pipe(observeOn(asapScheduler));

  async onClick(
    execute: (() => void) | undefined,
    routerLink: unknown[] | string | undefined,
    canExecute: boolean,
    routerLinkParams: NavigationExtras | undefined
  ): Promise<void> {
    if (!canExecute) return;
    if (execute) execute();
    else if (routerLink)
      await this.router.navigate(
        typeof routerLink === 'string' ? [routerLink] : routerLink,
        routerLinkParams
      );
  }
}
